import type {
  SearchFilterCondition,
  SearchFilterConditionForField,
  SearchFilterField,
} from 'lib-search-types';
import type { SearchFilterChangeEvent } from 'lib-search-ui';
import { useCallback } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { searchActions } from '../../../../../search.actions';
import { useSearchFilters } from '../../../searchFilters.context';

type UseSearchQuickFilterResult<
  TField extends SearchFilterField<'conversations'>,
> = {
  condition?: SearchFilterConditionForField<'conversations', TField>;
  handleChange: (event: SearchFilterChangeEvent) => void;
};

/**
 * Manages a single quick filter condition by field.
 *
 * Quick filters are a mirror of the advanced filters — they reflect the first
 * condition found for the given field in the shared draft state. If multiple
 * conditions exist for the same field (added via advanced mode), the quick
 * filter displays the first one.
 *
 * On change, both the draft (context) and Redux store are updated in one step,
 * so the filter applies immediately without needing "Apply filters".
 */
export function useSearchQuickFilter<
  TField extends SearchFilterField<'conversations'>,
>(field: TField): UseSearchQuickFilterResult<TField> {
  const dispatch = useAppDispatch();

  const { strategy, filters, upsertConditionByField, removeConditionByField } =
    useSearchFilters();

  const condition = filters.find(
    (f): f is SearchFilterConditionForField<'conversations', TField> =>
      f.field === field,
  );

  const handleChange = useCallback(
    ({ action, operator, values }: SearchFilterChangeEvent) => {
      const newCondition = {
        field,
        operator,
        values,
      } as SearchFilterCondition<'conversations'>;

      if (action === 'upsert') {
        upsertConditionByField(field, newCondition);
      } else {
        removeConditionByField(field);
      }

      dispatch(
        searchActions.params.update({
          filters: {
            strategy,
            filters:
              action === 'upsert'
                ? [...filters.filter((f) => f.field !== field), newCondition]
                : filters.filter((f) => f.field !== field),
          },
          page: 1,
        }),
      );
    },
    [
      dispatch,
      field,
      filters,
      removeConditionByField,
      strategy,
      upsertConditionByField,
    ],
  );

  return { condition, handleChange };
}
